import { pipe } from 'fp-ts/function';
import { array, option } from 'fp-ts';
import { NonEmptyArray } from 'fp-ts/NonEmptyArray';
import { PortfolioRateGroupOverview, useRepositoryQuery } from '@mews-commander/data-access';
import { Banner, Icon, IconButton, IconName, Stack, Title } from '@optimus-web/core';
import { lt, showSuccessAlert, useLocalizeProperty } from '@mews/framework';
import { TabbedContent } from '@mews-commander/ui';
import { DeleteLocalRateGroupContainer } from '../../sections/components/rateGroup/DeleteLocalRateGroupContainer';
import { EditRateGroupContainer } from '../../sections/components/rateGroup/EditRateGroupContainer';
import { ModalSidePanel } from '../../../../../components';
import {
    CurrentRateGroupContextProvider,
    CurrentServiceIdContextProvider,
    useCurrentRateGroupContext,
} from '../../../../rateGroups/contexts';
import { PoliciesTabContent } from '../../../../rateGroups/components/sidePanel/PoliciesTabContent';
import { CentralCreatePolicyLinks } from '../../../../rateGroups/components/forms/DefaultPolicyForm/CentralCreatePolicyLinks';
import {
    useEditDefaultPolicyDialogForm,
    useSettlementConfigurationEditDialogForm,
} from '../../../../rateGroups/components/forms';
import { usePolicyExceptionUpdate, useSettlementConfigurationUpdate } from '../../../../rateGroups/mutations';
import { WithDefaultPolicy } from '../../../../rateGroups/types';
import { useLocalRateGroupWithDefaultPoliciesGet } from './sidePanelTabs/queries';
import { RatesTabContent } from './sidePanelTabs/RatesTabContent';
import { DetailsTabContent } from './sidePanelTabs';
import { RateGroupTabValues } from './types';

const RATE_GROUP_TABS: NonEmptyArray<string> = ['details', 'policies', 'rates'];
const EditLocalDefaultPolicyButton = ({ defaultPolicy }: WithDefaultPolicy) => {
    const { mutateAsync } = usePolicyExceptionUpdate({
        invalidateQueries: {
            portfolioRateGroups: ['getOverview'],
            rateGroups: ['getAll'],
        },
    });

    const [renderDialog, toggleDialog] = useEditDefaultPolicyDialogForm({
        defaultPolicy,
        policyLinks: <CentralCreatePolicyLinks />,
        onSubmit: async (values) =>
            mutateAsync(values, {
                onSuccess: () => {
                    showSuccessAlert({ title: lt('RateGroupUpdatedSuccessMessage') });
                },
            }),
    });

    return (
        <>
            <IconButton icon={IconName.Edit} size="small" onClick={toggleDialog} type="button" />
            {renderDialog()}
        </>
    );
};

const EditLocalPaymentConfigurationButton = () => {
    const rateGroup = useCurrentRateGroupContext();

    const { mutateAsync } = useSettlementConfigurationUpdate(rateGroup.id, {
        invalidateQueries: {
            portfolioRateGroups: ['getOverview'],
            rateGroups: ['getAll'],
        },
    });

    const [renderDialog, toggleDialog] = useSettlementConfigurationEditDialogForm({
        ...rateGroup,
        currencyScope: 'all',
        title: lt('CentralRateGroupEditConfigurationFormTitle'),
        onSubmit: async (formValues) =>
            mutateAsync(formValues, {
                onSuccess: () => {
                    showSuccessAlert({ title: lt('RateGroupUpdatedSuccessMessage') });
                },
            }),
    });

    return (
        <>
            <IconButton icon={IconName.Edit} onClick={toggleDialog} size="small" type="button" />
            {renderDialog()}
        </>
    );
};

interface Props {
    closeSidePanel: () => void;
    initialTab?: RateGroupTabValues;
    selectedRateGroup?: PortfolioRateGroupOverview;
}

export const LocalRateGroupSidePanel = ({ closeSidePanel, initialTab, selectedRateGroup }: Props) => {
    const { data, isFetching } = useLocalRateGroupWithDefaultPoliciesGet({
        enterpriseId: selectedRateGroup?.enterpriseId,
        rateGroupId: selectedRateGroup?.id,
    });

    const rateIds = pipe(
        option.fromNullable(data?.policyExceptions),
        option.fold(
            () => [],
            array.flatMap((a) => a.rateIds || [])
        )
    );

    const { data: ratesData, isFetching: isFetchingRates } = useRepositoryQuery(
        'rates',
        'getAll',
        {
            enterpriseIds: selectedRateGroup?.enterpriseId ? [selectedRateGroup?.enterpriseId] : [],
            rateIds,
        },
        {
            enabled: !!(selectedRateGroup?.enterpriseId && array.isNonEmpty(rateIds)),
            select: ({ rates }) => rates,
        }
    );

    const localizeProperty = useLocalizeProperty();
    return (
        <ModalSidePanel
            headerActions={
                data && selectedRateGroup && !selectedRateGroup.isPortfolioManaged && selectedRateGroup.rates === 0 ? (
                    <DeleteLocalRateGroupContainer rateGroups={[selectedRateGroup]} onSuccess={closeSidePanel} />
                ) : null
            }
            content={
                <Stack vertical spacing="400">
                    {selectedRateGroup?.isPortfolioManaged ? (
                        <Banner
                            variant="info"
                            primaryText={lt('LinkedRateGroupLimitedEditingBannerTitle')}
                            secondaryText={lt('LinkedRateGroupLimitedEditingBannerDescription')}
                        />
                    ) : null}
                    <TabbedContent
                        tabs={RATE_GROUP_TABS}
                        tabLabels={{
                            details: lt('DetailPlural'),
                            policies: lt('GeneralPolicyPlural'),
                            rates: lt('RatePlural'),
                        }}
                        initialTab={initialTab}
                        inline
                        showDivider
                    >
                        {({ matchTabs }) => {
                            if (!(data && selectedRateGroup)) {
                                return null;
                            }

                            const rateGroupDetails = {
                                ordering: data.ordering,
                                creatorProfile: data.creatorProfile,
                                updaterProfile: data.updaterProfile,
                                createdUtc: data.createdUtc,
                                updatedUtc: data.updatedUtc,
                                description: localizeProperty(data.descriptions),
                                shortName: localizeProperty(data.shortNames),
                                rateGroupKey: data.externalIdentifier,
                            };

                            const contextValue = {
                                ...selectedRateGroup,
                                policyOverrideStatus: undefined,
                                ...data,
                                rates: ratesData || [],
                                activeRateCount: Number.NaN,
                                activeRates: [],
                            };

                            return (
                                <CurrentRateGroupContextProvider value={contextValue}>
                                    <CurrentServiceIdContextProvider value={data?.serviceId}>
                                        {matchTabs({
                                            details: () => (
                                                <DetailsTabContent
                                                    editRateGroupButton={
                                                        <EditRateGroupContainer
                                                            enterpriseIds={[selectedRateGroup.enterpriseId]}
                                                            linkedRateGroups={1}
                                                            rateGroup={data}
                                                            isPortfolioManaged={selectedRateGroup.isPortfolioManaged}
                                                        />
                                                    }
                                                    rateGroup={rateGroupDetails}
                                                    rates={selectedRateGroup.rates}
                                                    serviceKey={selectedRateGroup.serviceKey}
                                                />
                                            ),
                                            policies: () => (
                                                <PoliciesTabContent
                                                    policyExceptionsEditable={false}
                                                    editPaymentConfigurationButton={
                                                        <EditLocalPaymentConfigurationButton />
                                                    }
                                                    editDefaultPolicyButton={
                                                        contextValue.defaultPolicy ? (
                                                            <EditLocalDefaultPolicyButton
                                                                defaultPolicy={contextValue.defaultPolicy}
                                                            />
                                                        ) : null
                                                    }
                                                />
                                            ),
                                            rates: () => <RatesTabContent rateGroupId={data.id} />,
                                        })}
                                    </CurrentServiceIdContextProvider>
                                </CurrentRateGroupContextProvider>
                            );
                        }}
                    </TabbedContent>
                </Stack>
            }
            loading={isFetching || isFetchingRates}
            onClose={closeSidePanel}
            opened={!!selectedRateGroup}
            title={
                <Stack itemAlignment="center" spacing="100">
                    <Title level={2} wordBreak="break-all" ellipsis maxLines={2}>
                        {data?.name}
                    </Title>
                    {selectedRateGroup?.isPortfolioManaged ? <Icon value={IconName.CustomLink} /> : null}
                </Stack>
            }
            width="medium"
        />
    );
};
